/**
 * UpstreamProvenance — Phase 07.3 polish.
 *
 * Surfaces the engine DAG dependencies for the current engine, decorated
 * with each upstream's review status pill. Lets the reviewer see at a
 * glance whether a downstream engine is built on approved or
 * stale-after-rerun upstream data.
 *
 * Lifted out of CalculationReviewPanel.tsx into a proper component so
 * it can be unit-tested + reused. Behavior unchanged from the inline
 * version — pure refactor.
 */

import React from 'react';

import ReviewStatusPill from './ReviewStatusPill';
import type { UpstreamEngineSummary } from '../../../types/engineReview';

export interface UpstreamProvenanceProps {
  upstream?: UpstreamEngineSummary[];
}

export default function UpstreamProvenance({ upstream }: UpstreamProvenanceProps) {
  if (!upstream || upstream.length === 0) {
    return null;
  }

  return (
    <div className="rounded-lg border p-3" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)' }} data-testid="upstream-provenance">
      <div className="flex items-center gap-2 mb-2">
        <svg className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
        <span className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
          Depends on:
        </span>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {upstream.map((engine) => (
          // Name + status: two upstreams both "Not yet reviewed" were rendering as
          // two identical anonymous chips; the dependency name identifies each.
          <span key={engine.engine_id} className="inline-flex items-center gap-1.5">
            <span className="text-xs font-medium" style={{ color: 'var(--color-text-primary)' }}>
              {engine.engine_name}
            </span>
            <ReviewStatusPill
              status={engine.status}
              size="sm"
              testId={`upstream-${engine.engine_id}`}
            />
          </span>
        ))}
      </div>
    </div>
  );
}
